const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Formats a decimal string into USD currency display without using Number, parseFloat, or Intl numeric coercion.
 * Preserves high precision and very large values without floating point distortion or rounding.
 */
export function formatExactUSD(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) {
    return '—';
  }

  const trimmed = amount.trim();
  if (!trimmed) {
    return '—';
  }

  // Regex to match optional sign, digits, and optional decimal portion
  const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (!match) {
    return trimmed;
  }

  const sign = match[1] || '';
  const intPart = match[2];
  let fracPart = match[3];

  if (fracPart === undefined) {
    fracPart = '00';
  } else if (fracPart.length === 1) {
    fracPart = fracPart + '0';
  }

  // Insert thousands commas into integer part purely via regex on digit string
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${sign}$${formattedInt}.${fracPart}`;
}

/**
 * Formats period start calendar identifiers (e.g. "2026-10-01") directly without browser timezone conversion drift.
 */
export function formatPeriodIdentifier(
  periodStart: string | null | undefined,
  granularity: 'week' | 'month' = 'month',
): string {
  if (!periodStart) {
    return '—';
  }

  const trimmed = periodStart.trim();
  if (!trimmed) {
    return '—';
  }

  if (granularity === 'month') {
    const match = /^(\d{4})-(\d{2})/.exec(trimmed);
    if (match) {
      const year = match[1];
      const monthIndex = parseInt(match[2], 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${MONTH_NAMES[monthIndex]} ${year}`;
      }
    }
  } else if (granularity === 'week') {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
    if (match) {
      return `Week of ${match[1]}`;
    }
  }

  return trimmed;
}

/**
 * Formats an ISO instant timestamp for display in browser local time with an explicit (local time) note.
 */
export function formatBrowserTimestamp(
  isoTimestamp: string | null | undefined,
): string {
  if (!isoTimestamp) {
    return '—';
  }

  const trimmed = isoTimestamp.trim();
  if (!trimmed) {
    return '—';
  }

  try {
    const d = new Date(trimmed);
    if (isNaN(d.getTime())) {
      return trimmed;
    }
    return `${d.toLocaleString()} (local time)`;
  } catch {
    return trimmed;
  }
}
